import React, { useEffect } from 'react';
import { useDeviceSize } from 'common/lib/hooks/useDeviceSize';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import CompanyProfile from '../components/companyProfile';
import AdDescription from '../components/adDescription';
import ContentMap from '../components/map';
import EndNotes from '../components/endNotes';
import ImageCarousel from '../components/imageCarousel';
import InterestListButton from '../components/interestListButton';
import ProgressBar from '../components/progressBar';
import OpenHouse from '../components/openHouse';
import AdHeading from '../components/adHeading';
import AdFacilities from '../components/adFacilities';
import AdFinancing from '../components/adFinancing';
import NeighbourhoodProfile from '../components/neighbourhood';
import ImproveVisibilityPanel from '../components/improveVisibility';
import CollapsableText from '../components/collapsableText';
import ExternalContent from '../components/externalContent';
import UsefulLinks from '../components/usefulLinks';
import KeyInfo from '../components/keyInfo';
import SharingOptions from '../components/sharingOptions';
import SearchHeader from '../components/searchHeader';
import BackToProjectButton from '../components/backToProjectButton';

const AppPtsUnit = ({ data }) => {
    const {
        heading,
        master,
        finnkode: unitFinnkode,
        adStatus,
        apartmentNumber,
        facilities,
        descriptionList,
        calendarUrl,
        podletPath,
        isSold,
        imageHost,
        userId,
        layoutPath,
        lastUpdatedAt,
        externalAdId,
        moreInfoLinks,
        apiHost,
        pictureUrl,
        adType,
    } = data;

    const {
        streetAddress,
        localAreaName,
        viewings,
        postNumber,
        postArea,
        latitude,
        longitude,
        adStageStatusOne,
        adStageStatusTwo,
        adStageStatusThree,
        adStageStatusFour,
        finnkode: parentFinnkode,
        orgId,
        orderUrl,
        projectUrl,
        contentModule,
        createdBy,
        projectPhase,
        viewingRegistrationUrl,
        communicationEmail,
    } = master;

    const { isMobile, isDesktop } = useDeviceSize();

    const adDataForOrgProfile = {
        ...master,
        imageHost,
        apiHost,
        pictureUrl,
        moreInfoLinks: !_.isEmpty(moreInfoLinks) ? moreInfoLinks : master.moreInfoLinks,
    };

    useEffect(() => {
        if (window.hj) {
            window.hj('event', 'view_page_nybygg_pts_unit');
        }
    }, []);

    const mobileView = (
        <>
            <BackToProjectButton parentFinnkode={parentFinnkode} podletPath={podletPath} />
            <SharingOptions finnkode={unitFinnkode} heading={heading} />
            <AdHeading
                adStatus={adStatus}
                postNumber={postNumber}
                postArea={postArea}
                streetAddress={streetAddress}
                localAreaName={localAreaName}
                isSold={isSold}
                mainTitle={heading}
                secondaryTitle={apartmentNumber && `Enhetsnummer: ${apartmentNumber}`}
            />
            <ProgressBar
                adStageStatusOne={adStageStatusOne}
                adStageStatusTwo={adStageStatusTwo}
                adStageStatusThree={adStageStatusThree}
                adStageStatusFour={adStageStatusFour}
                projectPhase={projectPhase}
            />
            <AdFinancing data={data} />
            <KeyInfo data={data} />
            <InterestListButton adStatus={master.adStatus} communicationEmail={communicationEmail} />
            <OpenHouse
                dates={viewings}
                calendarUrl={calendarUrl}
                finnkode={unitFinnkode}
                address={streetAddress}
                viewingRegistrationUrl={viewingRegistrationUrl}
            />
            <AdFacilities facilities={facilities} />
            <CollapsableText>
                <AdDescription description={descriptionList} />
            </CollapsableText>
            <UsefulLinks projectUrl={projectUrl} orderUrl={orderUrl} moreInfoLinks={moreInfoLinks} orgId={orgId} />{' '}
        </>
    );

    const desktopView = (
        <div className="grid grid-cols-3 gap-32">
            <div className="col-span-2">
                <SharingOptions finnkode={unitFinnkode} heading={heading} />

                <AdHeading
                    adStatus={adStatus}
                    postNumber={postNumber}
                    postArea={postArea}
                    streetAddress={streetAddress}
                    localAreaName={localAreaName}
                    isSold={isSold}
                    mainTitle={heading}
                    secondaryTitle={apartmentNumber && `Enhetsnummer: ${apartmentNumber}`}
                />

                <AdFinancing data={data} />

                <KeyInfo data={data} />

                <AdFacilities facilities={facilities} />

                <CollapsableText>
                    <AdDescription description={descriptionList} />
                </CollapsableText>
            </div>

            <div className="col-span-1">
                <div className="top-64 sticky mt-16" style={{ zIndex: 2 }}>
                    {/* z-index is added here to override the default z-index of position sticky. This is 2 here because the FINN header has index 3, and the side column should not overlay the header */}
                    <BackToProjectButton parentFinnkode={parentFinnkode} podletPath={podletPath} />
                    <InterestListButton adStatus={master.adStatus} communicationEmail={communicationEmail} />

                    <OpenHouse
                        dates={viewings}
                        calendarUrl={calendarUrl}
                        finnkode={unitFinnkode}
                        address={streetAddress}
                        viewingRegistrationUrl={viewingRegistrationUrl}
                    />

                    <UsefulLinks
                        projectUrl={projectUrl}
                        orderUrl={orderUrl}
                        moreInfoLinks={moreInfoLinks}
                        orgId={orgId}
                    />
                </div>
            </div>
        </div>
    );

    return (
        <div className="page-container">
            <SearchHeader finnkode={unitFinnkode} adType={adType} />

            {isDesktop && (
                <ProgressBar
                    adStageStatusOne={adStageStatusOne}
                    adStageStatusTwo={adStageStatusTwo}
                    adStageStatusThree={adStageStatusThree}
                    adStageStatusFour={adStageStatusFour}
                    projectPhase={projectPhase}
                />
            )}
            <ImageCarousel />
            {isDesktop && userId === createdBy.toString() && (
                <ImproveVisibilityPanel
                    finnkode={parentFinnkode}
                    unitFinnkode={unitFinnkode}
                    orgId={orgId}
                    adStatus={adStatus}
                    layoutPath={layoutPath}
                />
            )}
            <CompanyProfile expandable data={adDataForOrgProfile} />

            {isDesktop ? desktopView : mobileView}

            <ContentMap
                mobile={isMobile}
                longitude={longitude}
                latitude={latitude}
                postNumber={postNumber}
                postArea={postArea}
                streetAddress={streetAddress}
            />
            <NeighbourhoodProfile finnkode={parentFinnkode} />
            <ExternalContent finnkode={parentFinnkode} contentModule={contentModule} />
            <CompanyProfile data={adDataForOrgProfile} />

            <EndNotes lastUpdatedAt={lastUpdatedAt} finnkode={unitFinnkode} externalAdId={externalAdId} />
        </div>
    );
};

AppPtsUnit.propTypes = {
    data: PropTypes.object.isRequired,
};

export default AppPtsUnit;
